import {
  Box,
  Button,
  Divider,
  Flex,
  Text,
  useMediaQuery
} from '@viveaesthetics/shared'
import React from 'react'
import Image from 'next/image'
import { formatSlugSegment } from '@viveaesthetics/shared-domain'
import { STATES_MAP } from '@viveaesthetics/elemy-shared-core/lib/constants/data'

import { ROUTES } from '@/constants/routes'
import { LINKS } from '@/constants/links'
import { StatesEnum } from '@/types/providers'
import { useAppContext } from '@/context/PreloadAppContext'

import { InternalLink } from '../InternalLink/InternalLink'
import { Title } from '../Title/Title'

import { FooterContent, FooterLinksWrapper, FooterWrapper } from './styles'

export const Footer = () => {
  const { isDesktop, isMobile } = useMediaQuery()
  const { branding } = useAppContext()
  const currentYear = new Date().getFullYear()

  const getStateLinkByState = (state: StatesEnum) => {
    return `${ROUTES.THERAPISTS}/${formatSlugSegment(STATES_MAP[state])}`
  }

  const sortedStateLinks = Object.values(StatesEnum)
    .sort((a, b) => a.localeCompare(b))
    .map(state => (
      <Button
        key={state}
        variant="link"
        href={getStateLinkByState(state)}
        as={InternalLink}
        data-test-id={`footer-${state.toLowerCase()}-link`}
        variantSize="small"
        noPadding
        justify="flex-start"
      >
        {STATES_MAP[state]}
      </Button>
    ))

  const totalColumns = 4
  const minItemsPerColumn = 2
  const totalItems = sortedStateLinks.length
  const columnsWithContent = Math.min(
    totalColumns,
    Math.ceil(totalItems / minItemsPerColumn)
  )
  const itemsPerColumn = Math.ceil(totalItems / columnsWithContent)

  const stateLinksColumns = Array.from(
    { length: columnsWithContent },
    (_, columnIndex) => {
      const startIndex = columnIndex * itemsPerColumn
      const endIndex = Math.min(startIndex + itemsPerColumn, totalItems)
      return (
        <FooterLinksWrapper
          key={columnIndex}
          $space="spacing16"
        >
          {sortedStateLinks.slice(startIndex, endIndex)}
        </FooterLinksWrapper>
      )
    }
  )

  // Add empty column if needed to reach totalColumns
  while (stateLinksColumns.length < totalColumns) {
    stateLinksColumns.push(
      <FooterLinksWrapper
        key={stateLinksColumns.length}
        $space="spacing16"
      />
    )
  }

  return (
    <FooterWrapper
      $padding={
        isDesktop
          ? ['spacing48', 'spacing16', 'spacing32', 'spacing16']
          : ['spacing48', 'spacing16', 'spacing32', 'spacing16']
      }
    >
      <FooterContent>
        <Box
          $padding={
            isDesktop
              ? ['spacing0', 'spacing0', 'spacing104', 'spacing0']
              : ['spacing0', 'spacing0', 'spacing32', 'spacing0']
          }
        >
          <Flex
            align="space-between"
            template={isDesktop ? [1, '0 0 83.6rem'] : [1, 1]}
            direction={isDesktop ? 'row' : 'column'}
          >
            <Box
              $padding={
                isDesktop
                  ? 'spacing0'
                  : ['spacing0', 'spacing0', 'spacing32', 'spacing0']
              }
            >
              <InternalLink
                href={LINKS.MAIN}
                data-test-id="footer-logo-link-to-homepage"
              >
                {branding.images.logoPrimary && (
                  <Image
                    src={branding.images.logoPrimary}
                    alt={`${branding.localizations?.appName} logo`}
                    width={
                      // Temporary quick solution
                      branding.localizations?.appName?.match('Resolute')
                        ? 120
                        : 177
                    }
                    height={20}
                  />
                )}
              </InternalLink>
            </Box>
            <Flex
              justify="flex-start"
              $space={isDesktop ? 'spacing48' : 'spacing32'}
              direction={isDesktop ? 'row' : 'column'}
            >
              <FooterLinksWrapper $space="spacing16">
                <Box
                  $padding={['spacing0', 'spacing0', 'spacing8', 'spacing0']}
                >
                  <Text
                    type="textS"
                    $color="textSecondary"
                  >
                    Get started
                  </Text>
                </Box>
                <Button
                  variant="link"
                  href={ROUTES.THERAPISTS}
                  data-test-id="footer-therapists-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Find a therapist
                </Button>
                <Button
                  variant="link"
                  href={LINKS.LOGIN}
                  data-test-id="footer-login-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Log in
                </Button>
              </FooterLinksWrapper>
              <FooterLinksWrapper $space="spacing16">
                <Box
                  $padding={['spacing0', 'spacing0', 'spacing8', 'spacing0']}
                >
                  <Text
                    type="textS"
                    $color="textSecondary"
                  >
                    Information
                  </Text>
                </Box>
                <Button
                  variant="link"
                  href={LINKS.ABOUT_US}
                  data-test-id="footer-about-us-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  About us
                </Button>
                <Button
                  variant="link"
                  href={LINKS.FOR_PROVIDERS}
                  data-test-id="footer-for-providers-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  For providers
                </Button>
              </FooterLinksWrapper>
              <FooterLinksWrapper $space="spacing16">
                <Box
                  $padding={['spacing0', 'spacing0', 'spacing8', 'spacing0']}
                >
                  <Text
                    type="textS"
                    $color="textSecondary"
                  >
                    Questions?
                  </Text>
                </Box>
                <Button
                  variant="link"
                  href={LINKS.FAQ}
                  data-test-id="footer-faq-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  FAQ
                </Button>
                <Button
                  variant="link"
                  href={LINKS.CONTACT_US}
                  data-test-id="footer-contact-us-link"
                  variantSize="small"
                  justify="flex-start"
                  noPadding
                >
                  Contact us
                </Button>
              </FooterLinksWrapper>
              <FooterLinksWrapper $space="spacing16">
                <Box
                  $padding={['spacing0', 'spacing0', 'spacing8', 'spacing0']}
                >
                  <Text
                    type="textS"
                    $color="textSecondary"
                  >
                    Social
                  </Text>
                </Box>
                <Button
                  variant="link"
                  href={LINKS.LINKEDIN}
                  data-test-id="footer-linkedin-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Linked In
                </Button>
                <Button
                  variant="link"
                  href={LINKS.FACEBOOK}
                  data-test-id="footer-facebook-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Facebook
                </Button>
                <Button
                  variant="link"
                  href={LINKS.INSTAGRAM}
                  data-test-id="footer-instagram-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Instagram
                </Button>
              </FooterLinksWrapper>
            </Flex>
          </Flex>
        </Box>

        <Divider $margin={isMobile ? 'spacing16' : 'spacing32'} />

        <Box
          $padding={
            isDesktop
              ? ['spacing0', 'spacing0', 'spacing104', 'spacing0']
              : ['spacing0', 'spacing0', 'spacing32', 'spacing0']
          }
        >
          <Flex
            align="space-between"
            template={isDesktop ? [1, '0 0 83.6rem'] : [1, 1]}
            direction={isDesktop ? 'row' : 'column'}
          >
            <Box
              $padding={
                isDesktop
                  ? 'spacing0'
                  : ['spacing0', 'spacing0', 'spacing32', 'spacing0']
              }
            >
              <Title
                type="title3"
                $color="textSecondary"
              >
                Find a therapist by state
              </Title>
            </Box>
            <Flex
              justify="flex-start"
              $space={isDesktop ? 'spacing48' : 'spacing32'}
              direction={isDesktop ? 'row' : 'column'}
            >
              {stateLinksColumns}
            </Flex>
          </Flex>
        </Box>

        <Divider $margin={isMobile ? 'spacing16' : 'spacing32'} />

        <Box
          $padding={
            isDesktop
              ? ['spacing0', 'spacing0', 'spacing104', 'spacing0']
              : ['spacing0', 'spacing0', 'spacing32', 'spacing0']
          }
        >
          <Flex
            align="space-between"
            template={isDesktop ? [1, '0 0 83.6rem'] : [1, 1]}
            direction={isDesktop ? 'row' : 'column'}
          >
            <Box
              $padding={
                isDesktop
                  ? 'spacing0'
                  : ['spacing0', 'spacing0', 'spacing32', 'spacing0']
              }
            >
              <Text
                type="labelL"
                $color="textDefault"
              >
                © {currentYear} {branding.localizations?.appName}
              </Text>
            </Box>
            <Flex
              justify="flex-start"
              $space={isDesktop ? 'spacing48' : 'spacing32'}
              direction={isDesktop ? 'row' : 'column'}
            >
              <FooterLinksWrapper $space="spacing16">
                <Button
                  variant="link"
                  href={LINKS.PRIVACY_POLICY}
                  data-test-id="footer-therapists-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Application Privacy Policy
                </Button>
                <Button
                  variant="link"
                  href={LINKS.WEBSITE_PRIVACY_POLICY}
                  data-test-id="footer-login-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Website Privacy Policy
                </Button>
              </FooterLinksWrapper>
              <FooterLinksWrapper $space="spacing16">
                <Button
                  variant="link"
                  href={LINKS.TERMS_OF_SERVICE}
                  data-test-id="footer-faq-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Terms of service
                </Button>
                <Button
                  variant="link"
                  href={LINKS.HIPAA_NOTICE_OF_PRIVACY_PRACTICES}
                  data-test-id="footer-contact-us-link"
                  variantSize="small"
                  justify="flex-start"
                  noPadding
                >
                  HIPAA Practices
                </Button>
              </FooterLinksWrapper>
              <FooterLinksWrapper $space="spacing16">
                <Button
                  variant="link"
                  href={LINKS.INFORMED_CONSENT}
                  data-test-id="footer-linkedin-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Informed Consent
                </Button>
                <Button
                  variant="link"
                  href={LINKS.PRACTICE_POLICIES}
                  data-test-id="footer-facebook-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Practice Policies
                </Button>
              </FooterLinksWrapper>
              <FooterLinksWrapper $space="spacing16">
                <Button
                  variant="link"
                  href={LINKS.PRIVACY_PRACTICE}
                  data-test-id="footer-linkedin-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Privacy Practices
                </Button>
                <Button
                  variant="link"
                  href={LINKS.RESPONSIBILITY}
                  data-test-id="footer-facebook-link"
                  variantSize="small"
                  noPadding
                  justify="flex-start"
                >
                  Responsibility
                </Button>
              </FooterLinksWrapper>
            </Flex>
          </Flex>
        </Box>

        <Divider $margin={isMobile ? 'spacing16' : 'spacing32'} />

        <Flex
          justify={isDesktop ? 'center' : 'flex-start'}
          align={isDesktop ? 'center' : 'flex-start'}
          $space="spacing4"
          direction={isDesktop ? 'row' : 'column'}
        >
          <Text
            $color="textTertiary"
            type="paragraphXS"
          >
            If you or someone you know is experiencing an emergency or crisis
            and needs immediate help, call 911 or go to the nearest emergency
            room. Additional crisis resources can be found{' '}
            <Button
              variant="link"
              variantSize="small"
              data-test-id="footer-crisis-resources-link"
              href={LINKS.ADDITIONAL_CRISIS_RESOURCES}
              noPadding
            >
              here.
            </Button>
          </Text>
        </Flex>
      </FooterContent>
    </FooterWrapper>
  )
}
